import { Archive, ChevronRight, Pencil } from "lucide-react";
import type { DashboardData, Recurring, Transaction } from "../../types";
import { dateLabel, money, shortName } from "../../lib/finance";
import Modal from "../../components/Modal";
import LoadingLabel from "../../components/LoadingLabel";
import { billNeed, type Need } from "../../lib/coverage";
import { useLedgerNeed } from "../../app/use-ledger-need";

/** One bill or regular income, with the transactions that settled it. */
export default function BillDetail({
  bill, data, ensure, today, onOpenTransaction, onClose, onEdit, onArchive,
}: {
  bill: Recurring;
  data: DashboardData;
  ensure: (need: Need) => unknown;
  today: string;
  onOpenTransaction: (t: Transaction) => void;
  onClose: () => void;
  onEdit?: () => void;
  onArchive?: () => void;
}) {
  const historyReady = useLedgerNeed(data, billNeed(data, bill.id), ensure);
  const income = bill.type === "Income";
  const history = (data.transactions || []).filter((t) => t.billId === bill.id).sort((a, b) => b.date.localeCompare(a.date));
  return (
    <Modal
      title={income ? "Recurring income" : "Bill"}
      onClose={onClose}
      headerActions={
        (onEdit || onArchive) && (
          <>
            {onEdit && (
              <button
                className="icon-button"
                onClick={onEdit}
                aria-label={`Edit ${bill.name}`}
              >
                <Pencil size={18} />
              </button>
            )}
            {onArchive && (
              <button
                className="icon-button danger-icon-button"
                onClick={onArchive}
                aria-label={`Archive ${bill.name}`}
              >
                <Archive size={18} />
              </button>
            )}
          </>
        )
      }
    >
      <div className="detail-hero">
        <h3>{bill.name}</h3>
        <p>{bill.frequency} · {bill.active ? (bill.nextDue < today ? (income ? "Late" : "Overdue") : `Next ${income ? "expected" : "due"} ${dateLabel(bill.nextDue)}`) : "Paused"}</p>
      </div>
      <dl className="detail-list">
        {bill.amount > 0 && <div><dt>Expected amount</dt><dd>{money(bill.amount, bill.currency)}</dd></div>}
        {bill.category && <div><dt>Category</dt><dd>{bill.category}{bill.subcategory ? ` · ${bill.subcategory}` : ""}</dd></div>}
        {bill.lastPaidDate && <div><dt>{income ? "Last received" : "Last paid"}</dt><dd>{dateLabel(bill.lastPaidDate)}</dd></div>}
      </dl>
      <div className="payee-section">
        <h4>{income ? "Received history" : "Payment history"}</h4>
        {!historyReady ? (
          <p className="muted empty-small" role="status"><LoadingLabel>Loading</LoadingLabel></p>
        ) : history.length ? (
          <div className="linked-txn-list">
            {history.map((t) => (
              <div className="linked-txn-row" key={t.id}>
                <button type="button" className="linked-txn-main" onClick={() => onOpenTransaction(t)}>
                  <span>
                    <strong>{dateLabel(t.date)}</strong>
                    <small>{shortName((data.accounts || []).find((a) => a.id === t.accountId)?.name || t.accountId)}</small>
                  </span>
                  <span>
                    {money(t.amount, t.currency)}
                    <ChevronRight size={15} />
                  </span>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted empty-small">{income ? "Nothing received against this yet." : "Nothing paid against this bill yet."}</p>
        )}
      </div>
    </Modal>
  );
}
